// nuolino-admin/src/lib/http.ts
// Wrapper fetch com refresh transparente (ADR-009 sliding refresh).
//
// Spec:
// - BFF gerencia access (nuolino_at) e refresh (nuolino_rt) em cookies httpOnly.
// - Frontend NUNCA toca nos tokens (XSS-safe).
// - 401 → POST /api/v1/auth/refresh (com credentials include) → retenta original 1x.
// - Se o refresh falhar → propaga 401 (AuthProvider detecta e desloga).
// - Concorrência: múltiplas requests 401 disparam UM refresh (deduplica).
// - Timeout configurável por chamada (default 15s, desliga com 0).
// - Errors tipados: HttpError com status + body parseado.

import { ApiError, NetworkError, TimeoutError, UnauthorizedError } from './http-errors';

export interface HttpRequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  /** Timeout em ms. 0 desativa. Default 15_000. */
  timeoutMs?: number;
  /** Quando true, não tenta refresh em 401. Usado para /auth/refresh e /auth/login. */
  skipRefresh?: boolean;
  /** Quando true, não deduplica refresh (uso em testes). */
  noDedupe?: boolean;
}

const DEFAULT_TIMEOUT_MS = 15_000;

let inflightRefresh: Promise<boolean> | null = null;

/** @internal — usado apenas em testes para resetar o cache de dedupe. */
export function __resetHttpDedupForTests(): void {
  inflightRefresh = null;
}

async function tryRefresh(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const res = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // resolve in microtask queue to allow concurrent waiters
      queueMicrotask(() => {
        inflightRefresh = null;
      });
    }
  })();
  return inflightRefresh;
}

function buildBody(body: unknown, headers: Record<string, string>): RequestInit['body'] {
  if (body === undefined || body === null) return undefined;
  if (typeof body === 'string' || body instanceof FormData || body instanceof Blob) {
    return body as RequestInit['body'];
  }
  if (!headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }
  return JSON.stringify(body);
}

async function parseBody(res: Response): Promise<unknown> {
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  if (contentType.startsWith('text/')) {
    return res.text();
  }
  return null;
}

async function executeRequest(
  url: string,
  init: RequestInit,
  timeoutMs: number,
  externalSignal: AbortSignal | undefined,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = timeoutMs > 0 ? setTimeout(() => controller.abort(new Error('timeout')), timeoutMs) : null;

  // Encadeia signals externos
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort(externalSignal.reason);
    else externalSignal.addEventListener('abort', () => controller.abort(externalSignal.reason), { once: true });
  }

  try {
    return await fetch(url, { ...init, signal: controller.signal, credentials: init.credentials ?? 'include' });
  } catch (err) {
    if ((err as Error).name === 'AbortError' || controller.signal.aborted) {
      throw new TimeoutError(`Request to ${url} timed out after ${timeoutMs}ms`);
    }
    throw new NetworkError(`Network error on ${url}: ${(err as Error).message}`);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export async function httpRequest<T = unknown>(
  method: string,
  path: string,
  options: HttpRequestOptions = {},
): Promise<T> {
  const { body, headers: rawHeaders = {}, timeoutMs = DEFAULT_TIMEOUT_MS, skipRefresh, noDedupe, ...rest } = options;
  const headers: Record<string, string> = { ...rawHeaders };
  const init: RequestInit = {
    ...rest,
    method,
    headers,
    body: buildBody(body, headers),
  };
  const externalSignal = rest.signal ?? undefined;

  let res = await executeRequest(path, init, timeoutMs, externalSignal);

  if (res.status === 401 && !skipRefresh) {
    const refreshed = noDedupe ? await tryRefresh() : await tryRefresh();
    if (refreshed) {
      res = await executeRequest(path, init, timeoutMs, externalSignal);
    } else {
      const parsed = await parseBody(res);
      throw new UnauthorizedError('Session expired', parsed);
    }
  }

  if (res.status === 401 && skipRefresh) {
    // Probe explícito (ex.: /me no boot): 401 = sessão expirada.
    const parsed = await parseBody(res);
    throw new UnauthorizedError('Unauthorized', parsed);
  }

  if (!res.ok) {
    const parsed = await parseBody(res);
    throw new ApiError(`HTTP ${res.status} on ${method} ${path}`, res.status, parsed);
  }

  const content = await parseBody(res);
  return content as T;
}

// Helpers ergonômicos.
export const http = {
  get: <T = unknown>(path: string, options?: HttpRequestOptions) => httpRequest<T>('GET', path, options),
  post: <T = unknown>(path: string, body?: unknown, options?: HttpRequestOptions) =>
    httpRequest<T>('POST', path, { ...options, body }),
  postForm: <T = unknown>(path: string, form: FormData, options?: HttpRequestOptions) =>
    httpRequest<T>('POST', path, { ...options, body: form }),
  put: <T = unknown>(path: string, body?: unknown, options?: HttpRequestOptions) =>
    httpRequest<T>('PUT', path, { ...options, body }),
  patch: <T = unknown>(path: string, body?: unknown, options?: HttpRequestOptions) =>
    httpRequest<T>('PATCH', path, { ...options, body }),
  delete: <T = unknown>(path: string, options?: HttpRequestOptions) =>
    httpRequest<T>('DELETE', path, options),
};
